import { BaseComponent } from "../../Core/BaseComponent/BaseComponent";
import { components } from "../../Components";
import { JSX } from "react";
import { User } from "./DataModels"
import { SearchBox } from "../SearchBox/SearchBox";
import { PagedResponse } from "@iconics/core";

export class UserPicker extends BaseComponent<IUserPickerProps, IUserPickerState>
{
    private static readonly minimumSearchTextLength = 3;
    private searchUsersTimer: NodeJS.Timeout | null = null;
    private get noUserSelectedError() { return (!this.props.searchText || this.state.selectedUser ? "" : (this.props.noUserSelectedLabel ?? UserPicker.noUserSelectedLabel)); }

    public static label = '';
    public static noUserSelectedLabel = '';

    constructor(props: IUserPickerProps)
    {
        super(props);
        this.state = {
            users: [],
            selectedUser: null,
            searchText: '',
        };
    }

    public async componentDidMount(): Promise<void>
    {
        await this.searchTextUpdated();
    }

    public componentWillUnmount(): void
    {
        clearTimeout(this.searchUsersTimer ?? undefined);
    }

    private async searchTextUpdated(): Promise<void>
    {
        clearTimeout(this.searchUsersTimer ?? undefined);
        this.searchUsersTimer = setTimeout(() => this.searchUsers(), 500);
    }

    private async searchUsers(): Promise<void>
    {
        const { searchText } = this.state;

        // important: don't search until enough characters have been entered; a short (or empty) search text matches too many users and an empty one would be treated as an exact match for any user with a blank email
        if (searchText.trim().length < UserPicker.minimumSearchTextLength)
        {
            await this.setStateAsync({ users: [], selectedUser: null });
            await this.props.onUserChange?.(null);
            return;
        }

        const users = await this.props.onUserSearch(searchText);

        // the search text may have changed while the search was in flight, in which case the response is stale and must be discarded
        if (this.state.searchText !== searchText)
        {
            return;
        }

        // important: we must only select a user if the email (and nothing else) matches the seartch text because this is the only field that uniquely identifies the user
        const selectedUser = users.find(i => i.email.toLowerCase() === searchText.toLowerCase()) ?? null;

        await this.setStateAsync({ users: users, selectedUser: selectedUser });
        await this.props.onUserChange?.(selectedUser);
    }

    private async searchTextChanged(text: string): Promise<void>
    {
        await this.setStateAsync({ searchText: text });
        await this.searchTextUpdated();
        await this.props.onChange(text);
    }

    public render(): JSX.Element
    {
        const { width } = this.props;
        return (
            <div style={{ ...(width != null && { width: width }) }}>
                <SearchBox
                    disabled={this.props.disabled ?? false}
                    items={this.state.users.map(u => u.email)}
                    placeholder={this.props.placeholder}
                    onInputValueChange={e => this.searchTextChanged(e.inputValue)}
                    label={this.props.label ?? UserPicker.label} />
                <div className="text-danger">{this.noUserSelectedError}</div>
            </div>
        );
    }
}

export interface IUserPickerProps
{
    noUserSelectedLabel?: string;
    disabled?: boolean;
    width?: string;
    searchText: string;
    label?: string;
    placeholder?: string;
    alternateStyle?: boolean;
    error?: boolean;
    helperText?: string;
    tenantId: string;
    onChange: (searchText: string) => Promise<void>;
    onUserChange?: (user: IUserPickerUser | null) => Promise<void>;
    onUserSearch: (searchText: string) => Promise<IUserPickerUser[]>;
}

export interface IUserPickerState
{
    users: IUserPickerUser[];
    selectedUser: IUserPickerUser | null;
    searchText: string;
}

export interface IUserPickerUser
{
    label: string;
    email: string;
    userName: string;
    firstName: string;
    lastName: string;
    displayName: string;
    company: string;
}
